import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

interface CueExtent { start: number; length: number; folder_num: string; }

/** The longest active cue may finish after the last cue by start time or after the source audio. */
export function timelineDuration(audioMs: number, folder: number, ...tracks: (CueExtent[] | undefined)[]): number {
    let end = Number.isFinite(audioMs) ? Math.max(0, audioMs) : 0;
    tracks.forEach((list) => list?.forEach((cue) => {
        if (Number(cue.folder_num) !== folder || !Number.isFinite(cue.start) || !Number.isFinite(cue.length)) return;
        end = Math.max(end, cue.start + Math.max(0, cue.length));
    }));
    return end;
}

/** Late metadata belongs to its original source/context; it must never resize a new folder. */
export function useOriginalAudioDuration(source: string | undefined, contextKey: string) {
    const current = useRef({ source, contextKey });
    current.current = { source, contextKey };
    const [value, setValue] = useState({ source, contextKey, duration: 0 });
    const setDuration = useCallback<Dispatch<SetStateAction<number>>>((next) => {
        if (current.current.source !== source || current.current.contextKey !== contextKey) return;
        setValue((previous) => {
            const old = previous.source === source && previous.contextKey === contextKey ? previous.duration : 0;
            const duration = typeof next === "function" ? next(old) : next;
            return { source, contextKey, duration: Number.isFinite(duration) ? Math.max(0, duration) : 0 };
        });
    }, [source, contextKey]);
    useEffect(() => {
        if (!source) return;
        let active = true;
        const audio = new Audio(source);
        audio.onloadedmetadata = () => { if (active) setDuration(audio.duration * 1000); };
        return () => { active = false; audio.onloadedmetadata = null; };
    }, [source, setDuration]);
    return { duration: value.source === source && value.contextKey === contextKey ? value.duration : 0, setDuration };
}
